import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage.jsx";
import { useTranslation } from "react-i18next";

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AuthContextType {
  user: string | null;
  userId: string;
  token: string | null;
  userRole: string | null;
  headquarterId: string | null;
  groupId: string | null;
  firstName: string | null;
  lastName: string | null;
  language: string | null;
  login: (email: string, password: string) => void;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    groupId: string,
    headquarterId: string,
    role: string,
    preferredLanguage: string
  ) => any;
  repeatVerification: () => void;
  logout: () => void;
  updateUser: (userData: any) => void;
  changeLanguage: (newLanguage: string) => Promise<void>;
  isLanguageReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const signIn = async (email, password) => {
  try {
    const response = await fetch(`api/auth/signin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const userData = await response.json();

    if (response.ok) {
      return userData;
    }
  } catch (error) {
    console.error("Error signing in:", error);
    throw new Error("An error occurred while signing in");
  }
};

const signUp = async (
  email,
  password,
  firstName,
  lastName,
  groupId,
  headquarterId,
  role,
  preferredLanguage
) => {
  try {
    const response = await fetch(`api/auth/signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        Email: email,
        Password: password,
        FirstName: firstName,
        LastName: lastName,
        GroupId: groupId,
        HeadquarterId: headquarterId,
        Role: role,
        PreferredLanguage: preferredLanguage.toUpperCase(),
      }),
    });

    let userData;

    if (response.ok) {
      userData = await response.json();
      return userData;
    } else {
      const errorData = await response.json();

      // Check for specific error messages
      if (errorData.error && errorData.error.message) {
        if (errorData.error.message === "EMAIL_EXISTS") {
          alert("This email address is already in use");
        } else {
          alert("Invalid email or password");
        }
      } else {
        alert("An unknown error occurred");
      }

      return null;
    }
  } catch (error) {
    console.log("Error during sign up:", error);
  }
};

const resendEmailVerification = async (email) => {
  try {
    const response = await fetch(`api/auth/resendEmailVerification`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(email),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error resending email verification:", error);
    throw new Error("An error occurred while resending email verification");
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [token, setToken] = useLocalStorage("token", null);
  const [userId, setUserId] = useLocalStorage("userId", null);
  const [userRole, setRole] = useLocalStorage("userRole", null);
  const [headquarterId, setHeadquarterId] = useLocalStorage(
    "headquarterId",
    null
  );
  const [groupId, setGroupId] = useLocalStorage("groupId", null);
  const [firstName, setFirstName] = useLocalStorage("firstName", null);
  const [lastName, setLastName] = useLocalStorage("lastName", null);
  const [language, setLanguage] = useLocalStorage("language", "en");
  const [isLanguageReady, setIsLanguageReady] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const initializeLanguage = async () => {
      if (i18n.language !== language) {
        await i18n.changeLanguage(language);
      }
      setIsLanguageReady(true);
    };
    initializeLanguage();
  }, [i18n, language]);

  const login = async (email: string, password: string) => {
    const userData = await signIn(email, password);

    setUser(email);
    setToken(userData.authToken);
    setUserId(userData.id);

    if (!userData.verification) {
      navigate("/verification");
      return;
    }

    setRole(userData.role);
    setHeadquarterId(userData.headquarterId);
    setGroupId(userData.groupId);
    setFirstName(userData.firstName);
    setLastName(userData.lastName);
    setLanguage(userData.preferredLanguage);

    userRole === "admin" ? navigate("/headquarterList") : navigate("/main");
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    groupId: string,
    headquarterId: string,
    role: string,
    preferredLanguage: string
  ) => {
    const userData = await signUp(
      email,
      password,
      firstName,
      lastName,
      groupId,
      headquarterId,
      role,
      preferredLanguage
    );

    if (!user && userData) {
      login(email, password);
    }

    return userData;
  };

  const repeatVerification = async () => {
    const result = await resendEmailVerification(user);
    if (!result) {
      navigate("/main");
      return;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setUserId(null);
    setRole(null);
    setHeadquarterId(null);
    setGroupId(null);
    setFirstName(null);
    setLastName(null);
    navigate("/login");
  };

  const updateUser = (userData: any) => {
    if (userData.firstName) setFirstName(userData.firstName);
    if (userData.lastName) setLastName(userData.lastName);
    if (userData.role) setRole(userData.role);
    if (userData.headquarterId) setHeadquarterId(userData.headquarterId);
    if (userData.groupId) setGroupId(userData.groupId);
    if (userData.preferredLanguage) setLanguage(userData.preferredLanguage);
  };

  const changeLanguage = async (newLanguage: string) => {
    await i18n.changeLanguage(newLanguage);
    setLanguage(newLanguage);
  };

  const value = useMemo(
    () => ({
      user,
      userId,
      token,
      userRole,
      headquarterId,
      groupId,
      firstName,
      lastName,
      language,
      login,
      register,
      repeatVerification,
      logout,
      updateUser,
      changeLanguage,
      isLanguageReady,
    }),
    [
      user,
      userId,
      token,
      userRole,
      headquarterId,
      groupId,
      firstName,
      lastName,
      language,
      isLanguageReady,
    ]
  );

  if (!isLanguageReady) {
    return <></>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
